import React from "react";
import Box from "@mui/material/Box";
import Columns from "../../../atoms/Columns";
import Column from "../../../atoms/Column";
import { Props } from "../../../pages/Crew/NoonReport/Create/types";
import { Link, TableCell, Typography } from "@mui/material";
import Steppr from "../../../atoms/Stepper";
import CommonText from "../../../atoms/CommonText";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CustomOwnerTableCell from "../../../atoms/CustomOwnerTableCell";

import {
  TableDataRow,
  Table3DataRow,
  Table6DataRow,
  SpareDataRow,
  TableAnyDataRow,
} from "../../../molecules/Owner/Detail/Detail";
import { formatToUtcYYYYMMDDHHMM } from "../../../../utils/formats";
import { convertDegreeToDirection } from "../../../../utils/convert-wind-direction-degree";
import { FuelType } from "../../../../gen/graphql/types";
import { filterAdditionalROBByFuelType } from "../../Crew/NoonReport/ROB/RobAdditional.module";
import { useDownloadRobFileQuery } from "./downloadRobFile.hook";
import { isNumeric } from "../../../../utils/type-checks";
import { useParams } from "react-router";
import Loading from "../../../atoms/Loading";
import { mediumGray, weightedGray } from "../../../../utils/color";
import { getFileName } from "../../../../utils/form-util";
import {
  FoConsumptionDefaultUsageEnumValues,
  MgoConsumptionDefaultUsageEnumValues,
} from "../../Crew/NoonReport/Consumption/consumptionForm.module";
import {
  additionalKeyStrings,
  createAdditionalArrays,
} from "../../Crew/NoonReport/detailAdditionalConsumption.module";

type FormBlockProps = Props & {
  pageTitle: string;
  isPreview: boolean;
  reportId: number;
  deleteReport?(): void;
  isSend: boolean;
  isServer?: boolean;
};

const Detail = (props: FormBlockProps): React.ReactElement => {
  const { reportId } = useParams<"reportId">();

  const report_id = isNumeric(reportId) ? Number(reportId) : 0;
  const downloadRobFile = useDownloadRobFileQuery(report_id);

  const foAdditionalArrays = createAdditionalArrays(
    additionalKeyStrings.FO,
    props.form
  );

  const mgoAdditionalArrays = createAdditionalArrays(
    additionalKeyStrings.MGO,
    props.form
  );

  const otherAdditionalArrays = createAdditionalArrays(
    additionalKeyStrings.OTHER,
    props.form
  );

  return (
    <Box component="form" noValidate autoComplete="off">
      <Loading isLoading={downloadRobFile.isLoading} />
      <Box sx={{ pt: 0.5, mx: 2 }}>
        <Columns
          sx={{
            display: "flex",
            alignItems: "top",
            justifycontent: "right",
            pb: 1,
          }}
        >
          <Column>
            <Typography
              component="h3"
              variant="subtitle1"
              align="center"
              sx={{
                lineHeight: 1,
                fontWeight: "bold",
                color: "#333333",
                fontSize: "1.5rem",
                verticalAlign: "super",
              }}
            >
              {props.pageTitle}
            </Typography>
          </Column>

          {props.isPreview || (
            <Column sx={{ width: "60%" }}>
              <Steppr step={1} />
            </Column>
          )}
        </Columns>
      </Box>
      <CommonText
        sx={{ color: "#333333", pl: 3, margin: "left", textAlign: "left" }}
      >
        {props.isPreview || `Confirm input data & click “Save” button.`}
      </CommonText>

      <Box sx={{ px: 2 }}>
        <Table
          color="inherit"
          sx={{ minWidth: 600, mx: 0, color: "#333333" }}
          aria-label="spanning table"
        >
          <TableHead>
            <TableRow sx={{ borderBottom: "#FFFFFF", height: "20px" }}>
              <CustomOwnerTableCell
                align="center"
                colSpan={3}
              ></CustomOwnerTableCell>
              <CustomOwnerTableCell />
              <CustomOwnerTableCell
                sx={{ color: "#AAAAAA", fontSize: "12px" }}
              ></CustomOwnerTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableDataRow
              title={"Vessel Name"}
              data={props.form.N_Vessel_name}
            />
            <TableDataRow title={"Voy#"} data={props.form.N_Voy} />
            <TableDataRow
              title={"Time Zone"}
              data={
                String(props.form.N_TimeZone_PorN) +
                props.form.N_TimeZone_HH +
                ":" +
                props.form.N_TimeZone_MM
              }
            />
            <TableDataRow title={"L or B"} data={props.form.N_LorB} />
            <TableDataRow title={"Port Name"} data={props.form.N_Noon_Port} />
            <TableDataRow title={"UNLOC"} data={props.form.N_UNLOC} />
            <TableDataRow title={"EU/UK"} data={props.form.N_EU_UK_port} />
            <TableDataRow title={"Port L/U"} data={props.form.N_Port_L_U} />
            <TableDataRow
              title={"Date & Time"}
              data={formatToUtcYYYYMMDDHHMM(props.form.N_DateTime)}
            />
            <TableDataRow
              title={"Lat./ Long"}
              data={
                String(props.form.N_Lat_Deg) +
                "-" +
                String(props.form.N_Lat_Min) +
                String(props.form.N_Lat_Direction) +
                " / " +
                String(props.form.N_Long_Deg) +
                "-" +
                String(props.form.N_Long_Min) +
                String(props.form.N_Long_Direction)
              }
            />
            <TableDataRow
              title={`Steam. mode`}
              data={props.form.N_Steam_mode}
            />
            <TableDataRow
              title={`Hrs. Propelling`}
              data={
                props.form.N_Hrs_propelling
                  ? Number.isNaN(props.form.N_Hrs_propelling)
                    ? null
                    : props.form.N_Hrs_propelling
                  : null
              }
              unitName={"hour"}
            />
            <TableDataRow
              title={`Distance travelled over ground (nautical mile)`}
              data={props.form.N_Dist_OG}
              unitName={"nautical mile"}
            />
            <TableDataRow
              title={`Speed over the ground (avg. knot)`}
              data={props.form.N_Speed_OG}
              unitName={"knot"}
            />
            <TableDataRow
              title={`Log speed (avg. knot)`}
              data={props.form.N_Speed_OW}
              unitName={"knot"}
            />
            <TableDataRow
              title={`M/E rpm - avg.`}
              data={props.form.N_ME_rpm_ave}
              unitName={"rpm"}
            />
            <TableDataRow
              title={`M/E rpm - total.`}
              data={props.form.N_ME_rpm_total}
              unitName={"rpm"}
            />
            <TableDataRow
              title={`M/E Revolution-Noon to Noon`}
              data={props.form.N_ME_Revolution}
            />
            <TableDataRow
              title={`Slip ratio (%)`}
              data={props.form.N_Slip}
              unitName={"%"}
            />
            <TableAnyDataRow
              topTitle={"FO Consumption per day"}
              title={FoConsumptionDefaultUsageEnumValues.concat(
                foAdditionalArrays.titles
              )}
              data={[
                props.form.N_FO_Consumption_per_day_ME,
                props.form.N_FO_Consumption_per_day_GE,
                props.form.N_FO_Consumption_per_day_BInc,
              ].concat(foAdditionalArrays.volumes)}
              unitName={Array(foAdditionalArrays.titles.length + 3).fill("mt")}
            />

            <TableAnyDataRow
              topTitle={"MGO Consumption per day"}
              title={MgoConsumptionDefaultUsageEnumValues.concat(
                mgoAdditionalArrays.titles
              )}
              data={[
                props.form.N_MGO_Consumption_per_day_ME,
                props.form.N_MGO_Consumption_per_day_GE,
                props.form.N_MGO_Consumption_per_day_Boiler,
              ].concat(mgoAdditionalArrays.volumes)}
              unitName={Array(mgoAdditionalArrays.titles.length + 3).fill("mt")}
            />

            <Table3DataRow
              topTitle={"LO Consumption per day"}
              title1={"MECO"}
              data1={props.form.N_LO_Consumption_per_day_MECO}
              unitName={"L"}
              title2={"MESO"}
              data2={props.form.N_LO_Consumption_per_day_MESO}
              title3={`GESO`}
              data3={props.form.N_LO_Consumption_per_day_GESO}
            />

            <TableAnyDataRow
              topTitle={"Other Consumption per day"}
              title={otherAdditionalArrays.titles}
              data={otherAdditionalArrays.volumes}
              unitName={Array(otherAdditionalArrays.titles.length).fill("mt")}
            />
            <TableDataRow title={`Weather`} data={props.form.N_Weather} />
            <TableDataRow
              title={`Wind direction`}
              data={convertDegreeToDirection(props.form.N_Wind_direction)}
            />
            <TableDataRow title={`BF scale`} data={props.form.N_BF_scale} />
            <TableDataRow title={`Swell`} data={props.form.N_Swell} />
            <TableDataRow
              title={`Noon Course`}
              data={props.form.N_Noon_Course}
              unitName={"degree"}
            />
            <TableDataRow
              title={`Dist. to go`}
              data={props.form.N_Dist_to_go}
              unitName={"nautical mile"}
            />
            <TableDataRow title={`Next port`} data={props.form.N_Next_port} />
            <TableDataRow
              title={`ETA`}
              data={formatToUtcYYYYMMDDHHMM(props.form.N_ETA)}
            />
            <TableDataRow
              title={`ETB`}
              data={formatToUtcYYYYMMDDHHMM(props.form.N_ETB)}
            />
            <TableDataRow
              title={`ETD`}
              data={formatToUtcYYYYMMDDHHMM(props.form.N_ETD)}
            />
            <TableDataRow
              title={`M/E Fuel index`}
              data={props.form.N_ME_Fuel_index}
              unitName={"%"}
            />
            <TableDataRow
              title={`M/E RPM`}
              data={props.form.N_ME_RPM}
              unitName={"rpm"}
            />
            <TableDataRow
              title={`M/E horsepower`}
              data={props.form.N_ME_horsepower}
              unitName={"kw"}
            />
            <TableDataRow
              title={`M/E power`}
              data={props.form.N_ME_power}
              unitName={"%"}
            />
            <TableDataRow
              title={`M/E power measurement method`}
              data={props.form.N_ME_power_measurement_method}
            />
            <Table3DataRow
              topTitle={"Running hours"}
              title1={"SOx scrubber"}
              data1={props.form.N_Running_hours_SOx_scrubber}
              unitName={"hour"}
              title2={"M/E EGR"}
              data2={props.form.N_Running_hours_ME_EGR}
              title3={`G/E SCR`}
              data3={props.form.N_Running_hours_GE_SCR}
            />

            <Table6DataRow
              topTitle={"ROB"}
              title1={"FO (mt)"}
              data1={props.form.N_ROB_FO}
              title2={"MGO (mt)"}
              data2={props.form.N_ROB_MGO}
              title3={"MECO (L)"}
              data3={props.form.N_ROB_MECO}
              title4={"MESO (L)"}
              data4={props.form.N_ROB_MESO}
              title5={"GESO (L)"}
              data5={props.form.N_ROB_GESO}
              title6={"FW (mt)"}
              data6={props.form.N_ROB_FW}
              unitName1={"mt"}
              unitName2={"L"}
            />

            <TableAnyDataRow
              topTitle={"Additional ROB"}
              title={
                filterAdditionalROBByFuelType(Object.values(FuelType), [
                  ...(props.form.N_Additional_ROB || []),
                ])?.map((v) => v.N_Fuel_Type) || []
              }
              data={
                filterAdditionalROBByFuelType(Object.values(FuelType), [
                  ...(props.form.N_Additional_ROB || []),
                ])?.map((v) => v.N_Fuel_volume) || []
              }
              unitName={Array(
                filterAdditionalROBByFuelType(Object.values(FuelType), [
                  ...(props.form.N_Additional_ROB || []),
                ])?.length
              ).fill("mt")}
            />

            <TableRow>
              <CustomOwnerTableCell sx={{ color: mediumGray, width: "250px" }}>
                {"FileName"}
              </CustomOwnerTableCell>
              <CustomOwnerTableCell />
              <CustomOwnerTableCell
                colSpan={2}
                sx={{
                  color: weightedGray,
                  wordBreak: "break-word",
                  whiteSpace: "pre-line",
                }}
              >
                {props.form.N_ROB_Filename && isNumeric(reportId) ? (
                  <Link onClick={() => downloadRobFile.onDownload()}>
                    {getFileName(props.form.N_ROB_Filename)}
                  </Link>
                ) : (
                  "-"
                )}
              </CustomOwnerTableCell>
              <CustomOwnerTableCell
                sx={{ color: weightedGray }}
              ></CustomOwnerTableCell>
            </TableRow>

            <TableDataRow
              title={`FO CO2 emission`}
              data={props.form.N_FO_CO2_emission_per_unit}
              unitName={"g/ton-nautical miles"}
            />
            <TableDataRow
              title={`MGO CO2 emission`}
              data={props.form.N_MGO_CO2_emission_per_unit}
              unitName={"g/ton-nautical miles"}
            />
            <TableDataRow
              title={`FO CO2 emission`}
              data={props.form.N_FO_CO2_emission}
              unitName={"mt"}
            />
            <TableDataRow
              title={`MGO CO2 emission`}
              data={props.form.N_MGO_CO2_emission}
              unitName={"mt"}
            />
            <TableDataRow title={`Remark`} data={props.form.N_Remark} />

            <TableRow>
              <TableCell
                rowSpan={21}
                sx={{
                  verticalAlign: "top",
                  color: "#888888",
                  borderBottom: "1px solid #C9D0DC",
                }}
              >
                Spare
              </TableCell>
            </TableRow>
            <SpareDataRow
              title={`Spare 1`}
              data={props.form.N_Spare_1}
              unitName={props.form.N_Spare_1_unit_name}
            />
            <SpareDataRow
              title={`Spare 2`}
              data={props.form.N_Spare_2}
              unitName={props.form.N_Spare_2_unit_name}
            />
            <SpareDataRow
              title={`Spare 3`}
              data={props.form.N_Spare_3}
              unitName={props.form.N_Spare_3_unit_name}
            />
            <SpareDataRow
              title={`Spare 4`}
              data={props.form.N_Spare_4}
              unitName={props.form.N_Spare_4_unit_name}
            />
            <SpareDataRow
              title={`Spare 5`}
              data={props.form.N_Spare_5}
              unitName={props.form.N_Spare_5_unit_name}
            />
            <SpareDataRow
              title={`Spare 6`}
              data={props.form.N_Spare_6}
              unitName={props.form.N_Spare_6_unit_name}
            />
            <SpareDataRow
              title={`Spare 7`}
              data={props.form.N_Spare_7}
              unitName={props.form.N_Spare_7_unit_name}
            />
            <SpareDataRow
              title={`Spare 8`}
              data={props.form.N_Spare_8}
              unitName={props.form.N_Spare_8_unit_name}
            />
            <SpareDataRow
              title={`Spare 9`}
              data={props.form.N_Spare_9}
              unitName={props.form.N_Spare_9_unit_name}
            />
            <SpareDataRow
              title={`Spare 10`}
              data={props.form.N_Spare_10}
              unitName={props.form.N_Spare_10_unit_name}
            />
            <SpareDataRow
              title={`Spare 11`}
              data={props.form.N_Spare_11}
              unitName={props.form.N_Spare_11_unit_name}
            />
            <SpareDataRow
              title={`Spare 12`}
              data={props.form.N_Spare_12}
              unitName={props.form.N_Spare_12_unit_name}
            />
            <SpareDataRow
              title={`Spare 13`}
              data={props.form.N_Spare_13}
              unitName={props.form.N_Spare_13_unit_name}
            />
            <SpareDataRow
              title={`Spare 14`}
              data={props.form.N_Spare_14}
              unitName={props.form.N_Spare_14_unit_name}
            />
            <SpareDataRow
              title={`Spare 15`}
              data={props.form.N_Spare_15}
              unitName={props.form.N_Spare_15_unit_name}
            />
            <SpareDataRow
              title={`Spare 16`}
              data={props.form.N_Spare_16}
              unitName={props.form.N_Spare_16_unit_name}
            />
            <SpareDataRow
              title={`Spare 17`}
              data={props.form.N_Spare_17}
              unitName={props.form.N_Spare_17_unit_name}
            />
            <SpareDataRow
              title={`Spare 18`}
              data={props.form.N_Spare_18}
              unitName={props.form.N_Spare_18_unit_name}
            />
            <SpareDataRow
              title={`Spare 19`}
              data={props.form.N_Spare_19}
              unitName={props.form.N_Spare_19_unit_name}
            />
            <SpareDataRow
              title={`Spare 20`}
              data={props.form.N_Spare_20}
              unitName={props.form.N_Spare_20_unit_name}
            />
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default Detail;
